import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  Signal,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { MediaResolved } from 'idt-ecommerce-api/catalog/products/medias/medias.models';
import { ProductsHelpers } from 'idt-ecommerce-api/catalog/products/products.helpers';
import { Product, ProductPartNumberDetails } from 'idt-ecommerce-api/catalog/products/products.models';
import { ProductsService } from 'idt-ecommerce-api/catalog/products/products.service';
import { RenderSetupDataService } from 'idt-ecommerce-api/render-setup-data/render-setup-data.service';
import { CMSEvent, CMSProductCarouselEvent } from 'render/models/cms-event.model';
import { IdtSharedRenderService } from 'render/render-shared/idt-shared-render.service';
import { getStringParam } from 'render/render-shared/ui/render-product-prices/render-product-prices-utils';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { CMSRenderEventsService } from 'render/services/cms-render-events.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { GrowthbookConfig, RenderGrowthbookConfigsService } from 'render/services/render-growthbook-configs/render-growthbook-configs.service';
import { iif, Observable, of } from 'rxjs';
import { IdtImage, PRODUCT_DESCRIPTION_DEFAULT_VALUE, PRODUCT_NAME_DEFAULT_VALUE } from 'shared/idt-components/idt-components-cms.model';
import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';
import { InditexWindow } from 'shared/models/inditex-window.models';
import { AmandaOptions, AssetsSharedService } from 'shared/services/assets-shared.service';
import { Link } from 'shared/services/links.model';
import { toNumber } from 'shared/utils/data-utils';
import { SwiperOptions } from 'swiper/types/swiper-options';

import { PieceProductBannerSettings, ProductImageLocations } from './models/piece-product-banner.models';
import { styleUrls, templateUrl } from './piece-product-banner-render.component.paths';


export interface ImageDetails extends ProductPartNumberDetails {
  location?: ProductImageLocations;
  viewStyle?: string;
  viewStyleMediaId?: string;
}


@Component({
  selector: 'piece-product-banner-render',
  templateUrl,
  styleUrls
})
export class PieceProductBannerRenderComponent
  extends BaseRenderComponent<PieceProductBannerSettings>
  implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  public product$!: Observable<Product | undefined>;
  public orientation$!: Observable<string>;
  public swiperConfig: SwiperOptions = {};
  public imageDetails: ImageDetails = {};

  public provider: string = 'none';
  public strategy: string = 'manual';
  public objective: string = 'control';

  private mainImageDetail?: ImageDetails;
  private extraImageDetail?: ImageDetails;
  private productSizesElement?: ElementRef<HTMLElement>;
  private productSizesResizeObserver?: ResizeObserver;
  private removeWindowResizeListener?: () => void;

  private lastProductId?: string;

  private readonly configs: Signal<GrowthbookConfig[]> = toSignal(
    this.renderGrowthbookService.configurations$,
    { initialValue: [] as GrowthbookConfig[] }
  );

  @ViewChild('productSizesBlock', { read: ElementRef })
  public set productSizesBlockRef(productSizesElement: ElementRef<HTMLElement> | undefined) {
    this.productSizesElement = productSizesElement;
    this.observeProductSizes();
    this.updateProductSizesHeightCssVar();
  }

  @HostBinding('style.--color-product-name')
  public get colorProductName(): string {
    if (this.piece?.settings?.general?.customStyles) {
      return getStringParam(this.configs(), 'content.home-product.primary-color', '#ffffff');
    }
    return '#ffffff';
  }


  constructor(
    @Inject('Window') private readonly window: InditexWindow,
    private readonly idtSharedRenderService: IdtSharedRenderService,
    private readonly productsService: ProductsService,
    private readonly renderConfigService: RenderConfigurationService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly cmsRenderEventsService: CMSRenderEventsService,
    public readonly renderByDevice: RenderByDeviceService,
    private readonly renderSetupDataService: RenderSetupDataService,
    private readonly renderGrowthbookService: RenderGrowthbookConfigsService,
    private readonly element: ElementRef,
    private readonly renderer: Renderer2
  ) {
    super();
    this.orientation$ = this.idtSharedRenderService.screenOrientation$;
  }

  @HostBinding('class.disable-interaction')
  public get disableInteraction(): boolean {
    return this.type === 'workspace';
  }

  public get hasToDisplayPrices(): boolean {
    return !!this.piece?.settings?.general?.displayProductPrices;
  }

  public get hasToDisplayProductName(): boolean {
    return !!this.piece?.settings?.general?.displayProductNameToggle;
  }

  public get hasToDisplayProductDescription(): boolean {
    return !!this.piece?.settings?.general?.displayProductDescriptionToggle;
  }

  public get hasToDisplayProductCart(): boolean {
    return !!this.piece?.settings?.general?.displayProductAddToCartToggle;
  }

  public get hasExtraImageEnabled(): boolean {
    return this.piece?.settings?.general?.extraImageToggle === true;
  }

  public get hasToDisplayProductPricesSelector(): boolean {
    return !!this.piece?.settings?.general?.displayProductSizesSelector;
  }

  public get hasToDisplayCustomImage(): boolean {
    return !!this.piece?.settings?.general?.customImageToggle;
  }

  public get hasToDisplayInspirationalText(): boolean {
    return !!this.piece?.settings?.general?.displayInspirationalTextToggle;
  }

  public get hasToApplyAkamaiOptimization(): boolean {
    return this.settings.general.objectFit === 'cover' && this.slot?.height === '100vh' && this.slot?.width === '100%';
  }

  public ngOnInit() {
    this.initTranslate();
    this.swiperConfig = {
      loop: false,
      navigation: false,
      pagination: false,
      slidesPerView: 'auto',
      direction: 'horizontal',
      spaceBetween: 5,
      autoplay: false,
      speed: 1000,
      centeredSlides: false,
      allowTouchMove: true
    };
  }

  public ngOnChanges(changes: SimpleChanges) {
    const { objectFit, objectPosition } = this.settings.general;

    if (!!objectFit) {
      this.renderer.setStyle(this.element.nativeElement, '--imageObjectFit', `${objectFit}`, 2);
    }

    if (!!objectPosition) {
      this.renderer.setStyle(this.element.nativeElement, '--imageObjectPosition', `${objectPosition}`, 2);
    }

    if (changes.piece && this.piece?.settings?.general?.productId !== this.lastProductId) {
      this.lastProductId = this.piece?.settings?.general?.productId;
      const hasToIncludeStockInformation = !!this.piece?.settings?.general?.displayLastSizeUnitsWarning;
      this.product$ = iif(
        () => !!this.lastProductId,
        this.productsService.productDetailOrNull(this.lastProductId as string, undefined, hasToIncludeStockInformation),
        of(undefined)
      );
    }
    this.initProductParts();
    this.deferProductSizesHeightUpdate();
  }

  public ngAfterViewInit(): void {
    this.removeWindowResizeListener = this.renderer.listen('window', 'resize', () => {
      this.updateProductSizesHeightCssVar();
    });
    this.observeProductSizes();
    this.updateProductSizesHeightCssVar();
  }

  public ngOnDestroy(): void {
    this.productSizesResizeObserver?.disconnect();
    this.removeWindowResizeListener?.();
  }

  public getPortraitImageSrc(): LazyDataInterface {
    const assetsUrl = this.renderConfigService.config.assetsUrl;
    const image = this.settings.general.portraitImage?.image;
    const akamaiOptions: AmandaOptions = {
      isWidthParamEnabled: this.renderConfigService.config.amandaImageVideoManagerEnabled
    };

    if (this.hasToApplyAkamaiOptimization) {
      akamaiOptions.width = this.applyAkamaiOptimization(this.settings.general.portraitImage);
    } else {
      akamaiOptions.width = this.assetsSharedService.getWidth(this.element);
    }

    return {
      src: this.assetsSharedService.getAssetUrl(image!, assetsUrl, akamaiOptions),
      height: toNumber(this.settings.general.portraitImage?.height),
      width: toNumber(this.settings.general.portraitImage?.width)
    };
  }

  public getLandscapeImageSrc(): LazyDataInterface {
    const assetsUrl = this.renderConfigService.config.assetsUrl;
    const image = this.settings.general.landscapeImage?.image;
    const akamaiOptions: AmandaOptions = {
      isWidthParamEnabled: this.renderConfigService.config.amandaImageVideoManagerEnabled
    };

    if (this.hasToApplyAkamaiOptimization) {
      akamaiOptions.width = this.applyAkamaiOptimization(this.settings.general.landscapeImage);
    } else {
      akamaiOptions.width = this.assetsSharedService.getWidth(this.element);
    }

    return {
      src: this.assetsSharedService.getAssetUrl(image!, assetsUrl, akamaiOptions),
      width: toNumber(this.settings.general.landscapeImage?.width),
      height: toNumber(this.settings.general.landscapeImage?.height)
    };
  }

  public getProductImageData(
    product: Product,
    imageDetails?: ImageDetails,
    width = 700
  ): LazyDataInterface | undefined {
    const effectiveImageDetails = imageDetails ?? this.imageDetails;
    const style = effectiveImageDetails?.style != null && Number.isInteger(+effectiveImageDetails.style)
      ? +effectiveImageDetails.style
      : undefined;
    const location = effectiveImageDetails?.location;
    let media: MediaResolved | undefined;
    if (effectiveImageDetails?.colorId) {
      const color = product.colors.find((pColor) => pColor.id === effectiveImageDetails?.colorId);
      if (color) {
        media = ProductsHelpers.firstImageForColor(color, location ?? 'product_page', width, undefined, style, this.getViewStyle(effectiveImageDetails));
      } else {
        return undefined;
      }
    } else {
      media = ProductsHelpers.firstImageForFirstColor(product.colors, location ?? 'product_page', width, undefined, style, this.getViewStyle(effectiveImageDetails));
    }
    return this.assetsSharedService.productImageData(media, width, this.renderConfigService.config);
  }

  public getBothImages(product: Product) {
    const images = [
      { imageData: this.getProductImageData(product, this.mainImageDetail), imageDetail: this.mainImageDetail },
      { imageData: this.getProductImageData(product, this.extraImageDetail), imageDetail: this.extraImageDetail }
    ];

    return images.every((image) => image.imageData) ? images : undefined;
  }

  public getProductName(productName: string): string {
    if (!!this.piece?.settings?.general.productName) {
      const template = this.piece.settings.general.productName;
      return template.replace(/{{productName}}/g, productName);
    }
    return PRODUCT_NAME_DEFAULT_VALUE.replace(/{{productName}}/g, productName);
  }

  public getProductDescription(productDescription: string): string {
    if (!!this.piece?.settings?.general.productDescription) {
      const template = this.piece.settings.general.productDescription;
      return template.replace(/{{productDescription}}/g, productDescription);
    }
    return PRODUCT_DESCRIPTION_DEFAULT_VALUE.replace(/{{productDescription}}/g, productDescription);
  }

  public getProductLink(product: Product, parts?: ProductPartNumberDetails): Link {
    const link: Link = {
      type: 'product',
      productId: product.id,
      productPartNumberDetails: this.getProductPartsFromDetails(parts)
    };

    if (this.piece?.settings?.general?.analytics?.salesAttribution) {
      link.extraQueryParams = [{
        key: 'xs',
        value: `${this.piece?.settings?.general?.analytics?.salesAttribution}`
      }];
    }

    return link;
  }

  public onProductClick(product: Product): void {
    const productsWithImage = {
      ...product,
      imagePath: this.getProductImageData(product)?.src
    };
    const cmsEvent: CMSEvent<CMSProductCarouselEvent> = {
      name: 'select_item',
      type: 'click',
      origin: this.piece?.settings?.general?.analytics?.origin || 'product-home',
      data: {
        index: 0,
        product: productsWithImage,
        sourceType: 'ver_producto',
        linkType: 'foto',
        strategy: this.piece?.settings?.general?.analytics?.strategy || this.strategy,
        provider: this.piece?.settings?.general?.analytics?.provider || this.provider,
        objective: this.piece?.settings?.general?.analytics?.objective || this.objective
      }
    };
    this.cmsRenderEventsService.emitEvent(cmsEvent);
  }

  public trackByIdx(idx: number): number {
    return idx;
  }

  public onMainEnter() {
    if (!this.renderByDevice.isMobile) {
      this.switchToParts('extra');
    }
  }

  public onMainLeave() {
    this.switchToParts('main');
  }

  public onDetailEnter(product: Product, variant: ProductPartNumberDetails) {
    if (!this.renderByDevice.isMobile) {
      this.switchToParts('main');
      this.imageDetails.colorId = variant.colorId;
      this.imageDetails.style = variant.style;
    }
  }

  public onDetailLeave() {
    this.switchToParts('main');
  }

  private initProductParts() {
    this.mainImageDetail = {
      colorId: this.piece?.settings?.general?.mainColorId,
      style: this.piece?.settings?.general?.mainStyle,
      location: this.piece?.settings?.general?.mainLocation,
      viewStyle: this.piece?.settings?.general?.mainViewStyle,
      viewStyleMediaId: this.piece?.settings?.general?.mainMediaId
    };

    this.extraImageDetail = {
      colorId: this.piece?.settings?.general?.extraColorId,
      style: this.piece?.settings?.general?.extraStyle,
      location: this.piece?.settings?.general?.extraLocation,
      viewStyle: this.piece?.settings?.general?.extraViewStyle,
      viewStyleMediaId: this.piece?.settings?.general?.extraMediaId
    };
    this.switchToParts('main');
  }

  private initTranslate(): void {
    const translations = this.renderSetupDataService.translations;
    if (this.piece?.settings?.general) {
      this.piece.settings.general.titleSize = translations?.['product-card.size'] ?? this.window.inditex?.text?.('sizes');
      this.piece.settings.general.titleSizeMobile = translations?.['product-card.size'] ?? this.window.inditex?.text?.('ItxProductSidebarComponent.size');
      this.piece.settings.general.oneSizeText = translations?.['product-card.unique-size'] ?? this.window.inditex?.text?.('ItxProductGridComponent.uniqueSize');
      this.piece.settings.general.petiteSizeText = translations?.['product-card.short-size-type'] ?? this.window.inditex?.text?.('GenericMessage.sizeTypeShort');
      this.piece.settings.general.petiteSizeDescription = translations?.['product-card.short-size-type-description'] ?? this.window.inditex?.text?.('ItxProductPage.shorter');
      this.piece.settings.general.regularSizeText = translations?.['product-card.normal-size-type'] ?? this.window.inditex?.text?.('GenericMessage.sizeTypeRegular');
      this.piece.settings.general.regularSizeDescription = translations?.['product-card.normal-size-type-description'] ?? this.window.inditex?.text?.('ItxProductPage.sameLength');
      this.piece.settings.general.tallSizeText = translations?.['product-card.long-size-type'] ?? this.window.inditex?.text?.('GenericMessage.sizeTypeLong');
      this.piece.settings.general.tallSizeDescription = translations?.['product-card.long-size-type-description'] ?? this.window.inditex?.text?.('ItxProductPage.longer');
      this.piece.settings.general.modelHeight = translations?.['product-card.model-height'] ?? this.window.inditex?.text?.('GenericMessage.modelHeight');
      this.piece.settings.general.modelSize = translations?.['product-card.model-size'] ?? this.window.inditex?.text?.('GenericMessage.modelSize');
      this.piece.settings.general.backSoonText = translations?.['product-card.notify-me'] ?? this.window.inditex?.text?.('ItxProductPage.notifyMe');
    }
  }

  private switchToParts(parts: 'main' | 'extra') {
    this.imageDetails = { ...(parts === 'main' || !this.hasExtraImageEnabled ? this.mainImageDetail : this.extraImageDetail) };
  }

  private getProductPartsFromDetails(details?: ImageDetails | ProductPartNumberDetails): ProductPartNumberDetails {
    const effectiveDetails = details ?? this.imageDetails;

    return {
      colorId: effectiveDetails.colorId,
      style: effectiveDetails.style
    };
  }

  private getViewStyle(imageDetails?: ImageDetails): string[] {
    switch (imageDetails?.viewStyle) {
      case 'silhouette-image':
        return ['s1', 's', 'c1', 'c'];
      case 'lookbook':
        return ['a10', 'c1', 'c'];
      case 'main':
        return ['a15', 'c1', 'c'];
      case 'extra-media-id':
        return imageDetails?.viewStyleMediaId ? [imageDetails.viewStyleMediaId] : [];
      default:
        return [];
    }
  }

  private applyAkamaiOptimization(image: IdtImage | null | undefined): number {
    return this.assetsSharedService.getOptimumImageWidth(
      toNumber(image?.height)!,
      toNumber(image?.width)!,
      this.window.innerHeight,
      this.assetsSharedService.getWidth(this.element)
    );
  }

  private deferProductSizesHeightUpdate(): void {
    if (typeof this.window.requestAnimationFrame === 'function') {
      this.window.requestAnimationFrame(() => this.updateProductSizesHeightCssVar());
      return;
    }
    setTimeout(() => this.updateProductSizesHeightCssVar());
  }

  private observeProductSizes(): void {
    this.productSizesResizeObserver?.disconnect();

    const nativeElement = this.productSizesElement?.nativeElement;
    const ResizeObserverConstructor = (globalThis as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver;
    if (!nativeElement || !ResizeObserverConstructor) {
      return;
    }

    const resizeObserver = new ResizeObserverConstructor(() => {
      this.updateProductSizesHeightCssVar();
    });
    this.productSizesResizeObserver = resizeObserver;
    resizeObserver.observe(nativeElement);
  }

  private updateProductSizesHeightCssVar(): void {
    if (!this.hasToDisplayProductCart || this.renderByDevice.isMobile) {
      this.renderer.setStyle(this.element.nativeElement, '--productSizesHeight', '0px', 2);
      return;
    }

    const productSizesHeight = Math.ceil(this.productSizesElement?.nativeElement.getBoundingClientRect().height ?? 0);
    this.renderer.setStyle(this.element.nativeElement, '--productSizesHeight', `${productSizesHeight}px`, 2);
  }

}
